import { EquipmentGroupAccordionProps } from 'app/types/ui/grouping'
import { ClientFrontlineConfig, EquipmentGroups, EquipmentType, GroupOfferingQuestions, Offer } from 'app/types/types'
import { compileOfferingQuestions } from '../nemo/offering/questions'
import { setContractType } from '../nemo/dependencies/contract-type'
import * as deps from '../nemo/dependencies/dependencies'
import { defaultContractScope } from '../components/organisms/ServiceConfigurationContent/constants'

const getEquipmentGroupsByID = (
  equipmentByGroups: EquipmentGroupAccordionProps[] | null,
  salesToolId: string
): EquipmentGroupAccordionProps | undefined =>
  equipmentByGroups!.find(categoryGroup => categoryGroup.id === salesToolId)

const getAllEquipmentCategory = (equipmentByCategory: EquipmentGroupAccordionProps): EquipmentType[] => {
  const categorySelections: EquipmentType[] = []
  equipmentByCategory.equipmentDetails.map(equipmentDetails => {
    const category = equipmentDetails.equipment?.equipmentCategory as EquipmentType
    if (category) {
      categorySelections.push(category)
    }
  })
  return Array.from(new Set(categorySelections))
}

export const applyCategorySelectionDefaults = (
  category: EquipmentType,
  selections: Record<string, string | string[] | null>
): Record<string, string | string[] | null> =>
  category === 'escalator' ? { ...selections, KCSM_CARE_JANITOR: '0', KCSM_VOICE_L: '0' } : selections

export const initializeSelections = (
  equipmentGroups: EquipmentGroups,
  equipmentByCategory: EquipmentGroupAccordionProps[] | null,
  offer: Offer,
  frontLineConfig: ClientFrontlineConfig,
  scopes?: string,
  resetSelections?: boolean
) => {
  const mutableEquipmentGroups = JSON.parse(JSON.stringify(equipmentGroups))
  Object.entries(mutableEquipmentGroups).map(([groupId, _]: [string, any]) => {
    const matchingCategoryGroup = getEquipmentGroupsByID(equipmentByCategory, groupId)
    const categorySelections: EquipmentType[] | undefined =
      matchingCategoryGroup && getAllEquipmentCategory(matchingCategoryGroup)
    if (mutableEquipmentGroups[groupId]) {
      const RESERVED_KEYS = new Set(['groupKind', 'name', 'salesToolId', 'scopes'])
      const existingCategories = Object.keys(mutableEquipmentGroups[groupId]).filter(
        key => !RESERVED_KEYS.has(key) && key !== 'scopes'
      )
      existingCategories.forEach(category => {
        if (!categorySelections?.includes(category as EquipmentType)) {
          delete mutableEquipmentGroups[groupId][category]
          if (mutableEquipmentGroups[groupId].scopes?.[category]) {
            delete mutableEquipmentGroups[groupId].scopes[category]
          }
        }
      })
    }

    const offeringSelections = frontLineConfig.offerSelections
    const offeringQuestions = compileOfferingQuestions(offeringSelections)
    if (categorySelections) {
      categorySelections.map((category: EquipmentType) => {
        const getScope = (
          scopes
            ? scopes
            : equipmentGroups[groupId].scopes
              ? equipmentGroups[groupId].scopes[category as keyof (typeof equipmentGroups)[typeof groupId]['scopes']] ||
                defaultContractScope
              : defaultContractScope
        ) as string
        const defaultSelections = offeringQuestions.group[category as keyof GroupOfferingQuestions]
        if (Array.isArray(defaultSelections)) {
          const selection = defaultSelections.reduce((acc: any, { key, default_values }) => {
            if (key) {
              acc[key] = default_values[getScope as keyof typeof default_values] || null
            }
            return deps.applySetterDependencies(defaultSelections, acc, offer, groupId)
          }, {})

          const selectionWithContractType = setContractType(applyCategorySelectionDefaults(category, selection))
          if (resetSelections || !mutableEquipmentGroups[groupId][category]) {
            mutableEquipmentGroups[groupId][category] = { selections: selectionWithContractType }
            mutableEquipmentGroups[groupId].scopes = {
              ...mutableEquipmentGroups[groupId].scopes,
              [category]: getScope
            }
          } else if (category === 'escalator') {
            mutableEquipmentGroups[groupId][category].selections = applyCategorySelectionDefaults(
              category,
              mutableEquipmentGroups[groupId][category].selections
            )
          }
        }
      })
    }
  })
  Object.entries(mutableEquipmentGroups).map(([groupId, group]) => {
    const isGroupEmpty = group as Record<string, any>
    if (isGroupEmpty.scopes && Object.keys(isGroupEmpty.scopes).length === 0) {
      delete mutableEquipmentGroups[groupId]
    }
  })
  return mutableEquipmentGroups
}

export const hasAnyScopeMissing = (equipmentGroups: EquipmentGroups): boolean =>
  Object.values(equipmentGroups).some(group => !group.scopes || Object.keys(group.scopes).length === 0)
